import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  getCities,
  getCountries,
  getCountryCode,
  getCountryData,
  getCountryShieldSvgUrl,
} from '../api/fetchDataFromCDN';
import i18n from '../i18n';
import { City, Country, CountryData } from '../types/game';
import {
  loadArchiveCompleted,
  loadCompleted,
  saveCompleted,
} from '../utils/roundsCompletedHelpers';

interface ITodaysCountryContext {
  todaysCountry: CountryData;
  countryList: Country[];
  cityList: City[];
  todaysCity?: City | null;
  isSpecialAprilFools2026: boolean;
  isPrankMode: boolean;
  isPrankRevealed: boolean;
  showPrankModal: boolean;
  setShowPrankModal: (show: boolean) => void;
  dismissPrank: () => void;
  markPrankRevealed: () => void;
  setCountryOverrideCode: (countryCode: string | null) => void;
  rounds: IRound[];
  completed: string[];
  saveCompletedRound: (roundName: string) => void;
  currentRound: IRound;
  navToRound: (round: IRound) => void;
  /** The day's wrap-up is a screen in its own right, not part of a round. */
  isDayEnd: boolean;
  goToDayEnd: () => void;
  dayString: string;
  isArchive: boolean;
}

/**
 * Stored in `current-round` like any round name, so the end screen survives a
 * reload or a trip to Archive/Replay. Deliberately not part of ROUND_LIST:
 * it is not playable and must not appear in the round navigation.
 */
export const DAY_END_ROUND_NAME = 'Day end';

export const TodaysCountryContext = createContext<ITodaysCountryContext | null>(
  null,
);

export interface IRound {
  name: string;
  emojis: string | string[];
  seed: string;
}

export const ROUND_LIST: IRound[] = [
  {
    name: 'Flag',
    emojis: '🎉',
    seed: '',
  },
  {
    name: 'Shape',
    emojis: '🗺️',
    seed: 'shape',
  },
  {
    name: 'Emblem',
    emojis: '🛡️',
    seed: 'emblem',
  },
  {
    name: 'Capital flag',
    emojis: '⛳️',
    seed: 'capital-flags',
  },
  {
    name: 'Neighbours',
    emojis: '🧭',
    seed: 'border-flag',
  },
  {
    name: 'Population & Currency',
    emojis: ['👫', '🪙'],
    seed: 'quiz',
  },
];

const APRIL_FOOLS_2026_DATES = new Set(['2026-04-01']);
const WORLD_GOVERNMENT_CODE = 'WG';
const APRIL_FOOLS_OVERRIDE_STORAGE_KEY =
  'flagle-april-fools-2026-country-override';
const PRANK_REVEALED_STORAGE_KEY = 'flagle-prank-revealed-2026';

const WORLD_GOVERNMENT_COUNTRY: CountryData = {
  code: WORLD_GOVERNMENT_CODE,
  latitude: 0,
  longitude: 0,
  name: 'World Government',
  names: { en: 'World Government' },
  flags: [],
  continent: [],
  population: 0,
  currencyData: { code: '', name: '', nameChoices: [] },
  borders: [],
  borderMode: 'nearby',
  autoUpdateBorders: false,
  size: 0,
  languageData: { languageSources: [], languages: [] },
  links: [],
};

const WORLD_GOVERNMENT_ENTRY: Country = {
  code: WORLD_GOVERNMENT_CODE,
  latitude: 0,
  longitude: 0,
  name: 'World Government',
};

const isAprilFools2026Day = (dayString: string) =>
  APRIL_FOOLS_2026_DATES.has(dayString);

const getInitialCountryOverrideCode = (
  dayString: string,
  isArchive: boolean,
) => {
  if (!isAprilFools2026Day(dayString)) return null;
  if (isArchive) return WORLD_GOVERNMENT_CODE;
  try {
    const raw = localStorage.getItem(APRIL_FOOLS_OVERRIDE_STORAGE_KEY);
    if (!raw) return WORLD_GOVERNMENT_CODE;
    const parsed = JSON.parse(raw) as {
      dayString?: string;
      countryCode?: string;
      dismissed?: boolean;
    };
    if (parsed.dayString === dayString) {
      if (parsed.dismissed) return null;
      if (parsed.countryCode) return parsed.countryCode;
    }
  } catch {
    // Fall back to default prank mode if storage is unavailable or invalid.
  }
  return WORLD_GOVERNMENT_CODE;
};

export const TodaysCountryProvider: FC<{
  dayString: string;
  isArchive: boolean;
  children: ReactNode;
}> = ({ dayString, isArchive, children }) => {
  const isSpecialAprilFools2026 = isAprilFools2026Day(dayString);
  const [todaysCountry, setTodaysCountry] = useState<CountryData>({
    code: '',
    latitude: 0,
    longitude: 0,
    name: '',
    names: {},
    flags: [],
    continent: [],
    population: 0,
    currencyData: {
      code: '',
      name: '',
      nameChoices: [],
    },
    borders: [],
    borderMode: 'nearby',
    autoUpdateBorders: false,
    size: 0,
    languageData: {
      languageSources: [],
      languages: [],
    },
    links: [],
  });

  const [countryList, setCountryList] = useState<Country[]>([]);
  const [cityList, setCityList] = useState<City[]>([]);
  const [countryOverrideCode, setCountryOverrideCodeState] = useState<
    string | null
  >(getInitialCountryOverrideCode(dayString, isArchive));
  const [countryOverrideData, setCountryOverrideData] =
    useState<CountryData | null>(null);

  useEffect(() => {
    setCountryOverrideCodeState(
      getInitialCountryOverrideCode(dayString, isArchive),
    );
    setCountryOverrideData(null);
  }, [dayString, isArchive]);

  const setCountryOverrideCode = (countryCode: string | null) => {
    setCountryOverrideCodeState(countryCode);
    if (isArchive || !isSpecialAprilFools2026) return;
    try {
      if (!countryCode) {
        localStorage.removeItem(APRIL_FOOLS_OVERRIDE_STORAGE_KEY);
        return;
      }
      localStorage.setItem(
        APRIL_FOOLS_OVERRIDE_STORAGE_KEY,
        JSON.stringify({ dayString, countryCode }),
      );
    } catch {
      // Ignore storage failures; in-memory state is still updated.
    }
  };

  useEffect(() => {
    let cancelled = false;

    const fetchCountryOverride = async () => {
      if (!countryOverrideCode) {
        setCountryOverrideData(null);
        return;
      }
      if (countryOverrideCode === WORLD_GOVERNMENT_CODE) {
        setCountryOverrideData(WORLD_GOVERNMENT_COUNTRY);
        return;
      }
      const data = await getCountryData(countryOverrideCode);
      if (!cancelled) {
        setCountryOverrideData(data);
      }
    };

    fetchCountryOverride();

    return () => {
      cancelled = true;
    };
  }, [countryOverrideCode]);

  const isPrankMode =
    isSpecialAprilFools2026 && countryOverrideCode === WORLD_GOVERNMENT_CODE;

  const [isPrankRevealed, setIsPrankRevealed] = useState(() => {
    if (!isSpecialAprilFools2026) return false;
    try {
      return localStorage.getItem(PRANK_REVEALED_STORAGE_KEY) === dayString;
    } catch {
      return false;
    }
  });

  const [showPrankModal, setShowPrankModal] = useState(false);

  const markPrankRevealed = () => {
    setIsPrankRevealed(true);
    try {
      localStorage.setItem(PRANK_REVEALED_STORAGE_KEY, dayString);
    } catch {
      // Ignore storage failures.
    }
    window.dispatchEvent(new CustomEvent('prank-revealed'));
  };

  const dismissPrank = () => {
    setCountryOverrideCodeState(null);
    setCountryOverrideData(null);
    if (!isArchive) {
      try {
        localStorage.setItem(
          APRIL_FOOLS_OVERRIDE_STORAGE_KEY,
          JSON.stringify({ dayString, dismissed: true }),
        );
      } catch {
        // Ignore storage failures.
      }
    }
  };

  const augmentedCountryList = useMemo(() => {
    if (!isPrankMode) return countryList;
    return [...countryList, WORLD_GOVERNMENT_ENTRY];
  }, [countryList, isPrankMode]);

  const effectiveTodaysCountry = countryOverrideData ?? todaysCountry;

  const todaysCity = useMemo(
    () =>
      cityList.filter(
        (c) => c.countryCode === effectiveTodaysCountry.code && c.capital,
      )[0],
    [cityList, effectiveTodaysCountry],
  );

  const [rounds, setRounds] = useState<IRound[]>(ROUND_LIST);
  const [currentRound, setCurrentRound] = useState<IRound>(ROUND_LIST[0]);

  const [isDayEnd, setIsDayEnd] = useState(false);

  const navToRound = (round: IRound) => {
    setIsDayEnd(false);
    setCurrentRound(round);
    if (!isArchive) {
      localStorage.setItem('current-round', round.name);
    }
  };

  const goToDayEnd = () => {
    setIsDayEnd(true);
    if (!isArchive) {
      localStorage.setItem('current-round', DAY_END_ROUND_NAME);
    }
    window.scrollTo({ top: 0 });
  };

  useEffect(() => {
    const fetchTodaysCountry = async () => {
      const countryCode = await getCountryCode(dayString);
      const countryData = await getCountryData(countryCode);
      setTodaysCountry(countryData);
    };
    const fetchCountryList = async () => {
      const countryList = await getCountries(i18n.language);
      setCountryList(countryList);
    };
    const fetchCityList = async () => {
      const cityList = await getCities(i18n.language);
      setCityList(cityList);
    };
    fetchTodaysCountry();
    fetchCountryList();
    fetchCityList();
  }, [dayString]);

  useEffect(() => {
    if (!effectiveTodaysCountry.code || !countryList.length || !cityList.length)
      return;
    const list = [...ROUND_LIST];

    if (!todaysCity?.flag) {
      const index = list.findIndex((r) => r.name === 'Capital flag');
      if (index >= 0) {
        list.splice(index, 1);
      }
    }

    setRounds(list);

    // check emblem exists
    const checkEmblem = async () => {
      const shieldUrl = getCountryShieldSvgUrl(effectiveTodaysCountry.code);
      const response = await fetch(shieldUrl);
      if (response.headers.get('content-type') === 'image/svg+xml') return;

      setRounds((prev) => {
        const newList = [...prev];
        const index = newList.findIndex((r) => r.name === 'Emblem');
        if (index >= 0) {
          newList.splice(index, 1);
        }
        return newList;
      });
    };
    checkEmblem();
  }, [
    cityList.length,
    countryList.length,
    todaysCity?.flag,
    effectiveTodaysCountry.code,
  ]);

  // completed rounds
  const [completed, setCompleted] = useState<string[]>([]);

  useEffect(() => {
    let roundsCompleted = [];
    if (isArchive) {
      roundsCompleted = loadArchiveCompleted();
    } else {
      roundsCompleted = loadCompleted()[dayString] ?? [];
    }

    setCompleted(roundsCompleted);

    // set current round based on what is completed
    if (isArchive) return;
    try {
      const storedCurrentRoundName = localStorage.getItem('current-round');
      if (!storedCurrentRoundName || !roundsCompleted.includes('Flag')) {
        navToRound(ROUND_LIST[0]);
        return;
      }
      if (storedCurrentRoundName === DAY_END_ROUND_NAME) {
        setIsDayEnd(true);
        return;
      }
      const current =
        ROUND_LIST.find((r) => r.name === storedCurrentRoundName) ??
        ROUND_LIST[0];
      navToRound(current);
    } catch {
      navToRound(ROUND_LIST[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayString, isArchive, rounds]);

  const saveCompletedRound = (round: string): void => {
    setCompleted((prev) => [...prev, round]);
    saveCompleted(dayString, round, isArchive);
  };

  if (!todaysCountry.name || !countryList.length) return null;
  if (countryOverrideCode && !countryOverrideData) return null;

  return (
    <TodaysCountryContext.Provider
      value={{
        todaysCountry: effectiveTodaysCountry,
        countryList: augmentedCountryList,
        cityList,
        todaysCity,
        isSpecialAprilFools2026,
        isPrankMode,
        isPrankRevealed,
        showPrankModal,
        setShowPrankModal,
        dismissPrank,
        markPrankRevealed,
        setCountryOverrideCode,
        rounds,
        completed,
        saveCompletedRound,
        currentRound,
        navToRound,
        isDayEnd,
        goToDayEnd,
        dayString,
        isArchive,
      }}
    >
      {children}
    </TodaysCountryContext.Provider>
  );
};

export const useTodaysCountry = () => {
  const context = useContext(TodaysCountryContext);
  if (!context) {
    throw new Error(
      'useTodaysCountry must be used within a TodaysCountryProvider',
    );
  }
  return context;
};
